var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { confirmationScreenHandler, messageBoxHandler } from '@shared/utils/AppHandlers';
import { DirectReportMessage } from '@shared/utils/DirectMessages/DirectReportMessage';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { ORDER_PARAMETER_PRODUCT_TYPE, ProductType } from '@shared/utils/Instruments/ProductType';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { OrderTif, OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { ErrorInformationStorage } from '../ErrorInformationStorage';
import { LOCALE_EN, Resources } from '@shared/localizations/Resources';
import { BaseSettingsUtils } from '../UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { OrderExecutorUtils } from './OrderExecutorUtils';
import { OrderEditRequestData } from '@shared/utils/Trading/OrderEditRequestData';
import { PositionCloseReqeustData } from '@shared/utils/Trading/PositionCloseRequestData';
class _OrderConfirmation {
    constructor() {
        this.CHANGE_TO_MARKET = 0;
        this.PLACE_OR_REPLACE = 1;
        this.REVERSE_POSITION = 2;
        this.processPlaceOrderConfirmationAsync = (orderEditObj) => __awaiter(this, void 0, void 0, function* () {
            const confirmationData = yield orderEditObj.getConfirmationData();
            const reqData = orderEditObj.getDataForRequest();
            const warningText = this.getWarningText(confirmationData);
            if (!this.isUseConfirmation(ConfirmationTypesEnum.OrderPlace) && warningText === '') {
                this.createRequestEventMessage(reqData, this.PLACE_OR_REPLACE);
                return yield Promise.resolve(true);
            }
            const result = yield this.showMessageBoxAsync(reqData, warningText, ConfirmationTypesEnum.OrderPlace, orderEditObj.needToShowWarningForcefully());
            if (result) {
                this.createRequestEventMessage(reqData, this.PLACE_OR_REPLACE);
            }
            return result;
        });
        this.getWarningText = (confirmationData) => {
            if (!BaseSettingsUtils.needToShowWarning(confirmationData)) {
                return '';
            }
            return OrderExecutorUtils.formatWarningText(confirmationData.warningArray);
        };
        this.isUseConfirmation = (confirmationType) => {
            return BaseSettingsUtils.useConfirmation(confirmationType);
        };
        this.showMessageBoxAsync = (reqData, warning, confirmationType, needToShowWarningForcefully) => __awaiter(this, void 0, void 0, function* () {
            return yield new Promise(function (resolve) {
                const okCallback = function (showNextTime) {
                    BaseSettingsUtils.updateConfirmation(confirmationType, showNextTime);
                    resolve(true);
                };
                const cancelCallback = function (showNextTime) {
                    BaseSettingsUtils.updateConfirmation(confirmationType, showNextTime);
                    resolve(false);
                };
                confirmationScreenHandler.Show(confirmationType, reqData, messageBoxHandler.msgType.Question, okCallback, cancelCallback, !needToShowWarningForcefully, // showNextTimeChB parameter (if need to show warning forcefully then no need in showNextTime CheckBox)
                false, warning);
            });
        });
        this.createRequestEventMessage = (reqData, modifier) => {
            var _a;
            try {
                const objArr = [];
                let header;
                let quantity;
                let panelId;
                let paramDict;
                let requestAccount = null;
                let requestInstrument = null;
                let requestOperationType = null;
                let requestTifType = null;
                let requestTifExpDate = null;
                let requestIsAllowOTHTrading = null;
                if (reqData instanceof OrderEditRequestData) {
                    requestAccount = reqData.account;
                    requestInstrument = reqData.instrument;
                    requestOperationType = reqData.side;
                    requestTifType = reqData.tif.type;
                    requestTifExpDate = reqData.tif.expirationTime;
                    panelId = reqData.placedFrom
                        ? reqData.placedFrom
                        : reqData.position != null
                            ? PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK
                            : reqData.order != null
                                ? PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK
                                : PlacedFrom.WEB_OTHER;
                    quantity = reqData.quantity ? reqData.quantity : null;
                    paramDict = reqData.parameterDict;
                    objArr.push(reqData);
                }
                else if (reqData instanceof PositionCloseReqeustData) {
                    requestAccount = reqData.position.Account;
                    requestInstrument = reqData.position.Instrument;
                    requestOperationType = reqData.position.BuySell;
                    requestTifType = reqData.position.TimeInForce;
                    quantity = reqData.quantityToClose;
                    requestIsAllowOTHTrading = reqData.isAllowOTHTrading;
                    panelId = reqData.placedFrom;
                    objArr.push(reqData.position);
                }
                else {
                    const args = reqData[0];
                    if (reqData[2] === undefined) {
                        panelId = reqData[1];
                        quantity = null;
                    }
                    else {
                        panelId = reqData[2];
                        quantity = reqData[1];
                    }
                    if (Array.isArray(args)) {
                        for (const arg of args) {
                            objArr.push(arg);
                        }
                    }
                    else {
                        objArr.push(args);
                    }
                }
                for (let i = 0; i < objArr.length; i++) {
                    const obj = objArr[i];
                    const acc = !isNullOrUndefined(requestAccount) ? requestAccount : obj.Account;
                    const ins = !isNullOrUndefined(requestInstrument) ? requestInstrument : obj.Instrument;
                    const operationType = !isNullOrUndefined(requestOperationType) ? requestOperationType : obj.BuySell;
                    const tifType = !isNullOrUndefined(requestTifType) ? requestTifType : obj.TimeInForce;
                    const isAllowOTHTrading = !isNullOrUndefined(requestIsAllowOTHTrading)
                        ? requestIsAllowOTHTrading
                        : obj.isAllowOTHTrading;
                    let orderType = obj.OrderType != null
                        ? obj.OrderType
                        : obj.orderTypeId != null
                            ? obj.orderTypeId
                            : obj.getOrderTypeId();
                    let ordTypeStr = OrderUtils.getOrderTypeLocalizationKey(orderType);
                    let tifStr = OrderTifMap[tifType];
                    const prodType = (_a = obj.productType) !== null && _a !== void 0 ? _a : obj.ProductType;
                    const productTypeStr = !isNullOrUndefined(prodType)
                        ? prodType !== ProductType.General
                            ? InstrumentUtils.GetLocalizedProductType(ins, prodType)
                            : null
                        : null;
                    let qtValue = null;
                    if (quantity && ins) {
                        qtValue = Quantity.toLots(quantity, ins);
                    }
                    if (modifier === this.CHANGE_TO_MARKET || obj.isPosition) {
                        // replace to market action (orderType -> Market)
                        orderType = OrderType.Market; // TODO МБ ^
                        ordTypeStr = OrderUtils.getOrderTypeLocalizationKey(orderType);
                    }
                    if (tifType === OrderTif.GTD) {
                        // добавляем дату к GTD tif-у
                        const expTime = !isNullOrUndefined(requestTifExpDate) ? requestTifExpDate : obj.ExpireAt;
                        const format = ins.DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SHOW_TIME_FOR_GTD)
                            ? 'MM.DD.YYYY HH:mm'
                            : 'MM.DD.YYYY'; // нужен именно формат с месяцами на первом месте для корректного парсинга в ReportMessageTooltip.fixEntries:707 #98285
                        const dtString = DateTimeUtils.formatDate(expTime, format);
                        tifStr += ' ' + dtString;
                    }
                    const user = { Label: 'User', Value: acc ? acc.userLogin : null };
                    const account = { Label: 'Account', Value: acc ? acc.FullAccString : null };
                    const operation = { Label: 'Operation', Value: OrderUtils.getBuySellStr(operationType) };
                    const orderId = {
                        Label: 'Order number',
                        Value: obj.PositionId != null && obj.PositionId !== '-1'
                            ? ''
                            : obj.OrderNumber
                                ? obj.OrderNumber
                                : obj.order
                                    ? obj.order.OrderNumber
                                    : ''
                    };
                    const positionId = {
                        Label: 'Position ID',
                        Value: obj.PositionId != null && obj.PositionId !== '-1' ? obj.PositionId : ''
                    };
                    const instrument = { Label: 'Instrument', Value: ins ? ins.DisplayShortName() : '' };
                    const instrType = {
                        Label: 'Symbol type',
                        Value: ins ? InstrumentUtils.getInstrumentTypeStringLocalized(ins.InstrType) : ''
                    };
                    const route = {
                        Label: 'Route',
                        Value: '' /* obj.Route ? DataCache.Routes.getRouteById(obj.Route).Name : (ins ? DataCache.Routes.getRouteById(ins.Route).Name : "") */
                    }; // #85093
                    const cash = { Label: 'reports.Cash', Value: obj.isCashMode ? acc === null || acc === void 0 ? void 0 : acc.formatPrice(obj.cash) : '' };
                    const amount = {
                        Label: 'Amount',
                        Value: qtValue !== null ? qtValue : obj.position ? obj.position.Amount : obj.Amount
                    };
                    const price = {
                        Label: 'Price',
                        Value: obj.Price
                            ? obj.Price
                            : paramDict.limitPrice
                                ? ins
                                    ? ins.formatPrice(paramDict.limitPrice)
                                    : paramDict.limitPrice
                                : null
                    };
                    const stopPrice = {
                        Label: 'Stop price',
                        Value: (paramDict === null || paramDict === void 0 ? void 0 : paramDict.stopPrice)
                            ? ins
                                ? ins.formatPrice(paramDict.stopPrice)
                                : paramDict.stopPrice
                            : null
                    };
                    let limitPriceForStopLimitOrder = null;
                    if (reqData instanceof OrderEditRequestData && isValidNumber(reqData.limitPriceForStopLimitOrder)) {
                        limitPriceForStopLimitOrder = !isNullOrUndefined(ins)
                            ? ins.formatPrice(reqData.limitPriceForStopLimitOrder)
                            : reqData.limitPriceForStopLimitOrder;
                    }
                    const limitPriceForSecondOrder = { Label: 'Limit price', Value: limitPriceForStopLimitOrder };
                    const stopPriceForSecondOrder = { Label: 'Stop price', Value: null };
                    const type = { Label: 'Order type', Value: !ordTypeStr.includes('type') ? ordTypeStr : '' };
                    const overnight = {
                        Label: 'property.OvernightExecution',
                        Value: !isNullOrUndefined(isAllowOTHTrading)
                            ? Resources.getResource(isAllowOTHTrading ? 'property.True' : 'property.False')
                            : null
                    };
                    const tif = { Label: 'TIF', Value: tifStr };
                    const time = { Label: 'Time', Value: Date.now().toString() };
                    const clientPanelId = {
                        Label: 'Send from',
                        Value: 'reports.' + this.getKeyByValue(PlacedFrom, panelId)
                    };
                    const operation2 = { Label: 'Operation', Value: null }; // for OCO
                    const boundTo = { Label: 'Bound to', Value: null };
                    let slLabel = null;
                    let sllLabel = null;
                    let tpLabel = null;
                    let slTrigger = null;
                    let tpTrigger = null;
                    const productType = { Label: ORDER_PARAMETER_PRODUCT_TYPE, Value: productTypeStr };
                    const leverage = { Label: 'Leverage', Value: OrderUtils.GetFormattedLeverage(obj.leverageValue) };
                    if (orderType === OrderType.Market) {
                        price.Value = null;
                    }
                    if (cash.Value !== '') {
                        amount.Value = null;
                    }
                    if (orderType === OrderType.OCO) {
                        if (obj.ocoCustomOrdersData) {
                            // from Chart
                            const dateFormat = ins.DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SHOW_TIME_FOR_GTD)
                                ? 'DD.MM.YYYY HH:mm'
                                : 'DD.MM.YYYY';
                            const OCOData = obj.ocoCustomOrdersData;
                            operation.Value = OrderUtils.getBuySellStr(OCOData.firstOrderSide);
                            operation2.Value = OrderUtils.getBuySellStr(OCOData.secondOrderSide);
                            let tifStr1 = OrderTifMap[OCOData.firstOrderTif.type];
                            if (OCOData.firstOrderTif.type === OrderTif.GTD) {
                                // с избытком учитывая что указать TIF при торговле с Chart нельзя
                                tifStr1 += ' ' + DateTimeUtils.formatDate(OCOData.firstOrderTif.expirationTime, dateFormat);
                            }
                            tif.Value = tifStr1;
                            if (OCOData.secondOrderLimitPriceForStopLimit) {
                                limitPriceForSecondOrder.Label = 'Limit price';
                                limitPriceForSecondOrder.Value = ins
                                    ? ins.formatPrice(OCOData.secondOrderLimitPriceForStopLimit)
                                    : OCOData.secondOrderLimitPriceForStopLimi;
                                stopPriceForSecondOrder.Label = 'Stop price';
                                stopPriceForSecondOrder.Value = stopPrice.Value;
                            }
                            else {
                                if (OCOData.secondOrderType === OrderType.Limit) {
                                    limitPriceForSecondOrder.Label = 'Price';
                                    limitPriceForSecondOrder.Value = (paramDict === null || paramDict === void 0 ? void 0 : paramDict.stopPrice)
                                        ? ins
                                            ? ins.formatPrice(paramDict.stopPrice)
                                            : paramDict.stopPrice
                                        : null;
                                }
                            }
                            if (OCOData.firstOrderLimitPriceForStopLimit) {
                                price.Label = 'Stop price';
                                stopPrice.Label = 'Limit price';
                                stopPrice.Value = ins
                                    ? ins.formatPrice(OCOData.firstOrderLimitPriceForStopLimit)
                                    : OCOData.firstOrderLimitPriceForStopLimit;
                            }
                            else {
                                if (OCOData.firstOrderType === OrderType.Limit) {
                                    price.Label = 'Price';
                                    price.Value = paramDict.limitPrice
                                        ? ins
                                            ? ins.formatPrice(paramDict.limitPrice)
                                            : paramDict.limitPrice
                                        : null;
                                    stopPrice.Value = null;
                                }
                            }
                        }
                        else {
                            // from OE
                            operation2.Value = operation.Value;
                        }
                    }
                    if (paramDict) {
                        // SL/TP Data
                        const iSlTpEditParams = paramDict.sltp;
                        const sltpHolder = iSlTpEditParams.sltpHolder;
                        const triggersShV = sltpHolder.SLTPTriggerShortValue;
                        if (!sltpHolder.isSlEmpty()) {
                            let slText;
                            let sllText;
                            if (sltpHolder.isSlAbsolute()) {
                                slText = 'SL price';
                                sllText = 'SL limit price';
                            }
                            else {
                                slText = sltpHolder.isTrailingStop() ? 'Tr. SL offset' : 'SL offset';
                                sllText = 'SL limit offset';
                            }
                            const slVisualOffsetType = SlTpUtils.getSLVisualOffsetType(ins, sltpHolder);
                            const slVisualValue = SlTpUtils.toVisualValue(sltpHolder.StopLossPriceValue, ins, slVisualOffsetType);
                            const slVisualValueFormatted = SlTpUtils.formatSLTPValue(slVisualValue, ins, slVisualOffsetType);
                            let slVisualValueDecsr = Resources.getResource(SlTpUtils.getLocalizationForOffsetMode(slVisualOffsetType));
                            if (isValidString(slVisualValueDecsr)) {
                                slVisualValueDecsr = ` ${slVisualValueDecsr}`;
                            }
                            slLabel = { Label: slText, Value: `${slVisualValueFormatted} ${slVisualValueDecsr}` };
                            if (!sltpHolder.isSllEmpty()) {
                                const sllVisualValue = SlTpUtils.toVisualValue(sltpHolder.StopLossLimitPriceValue, ins, slVisualOffsetType);
                                const sllVisualValueFormatted = SlTpUtils.formatSLTPValue(sllVisualValue, ins, slVisualOffsetType);
                                sllLabel = { Label: sllText, Value: `${sllVisualValueFormatted} ${slVisualValueDecsr}` };
                            }
                            slTrigger = {
                                Label: 'SL trigger',
                                Value: SLTPTrigger.GetTextValue(triggersShV, operationType, true)
                            };
                        }
                        if (!sltpHolder.isTpEmpty()) {
                            const tpText = sltpHolder.isTpAbsolute() ? 'TP price' : 'TP offset';
                            const tpVisualOffsetType = SlTpUtils.getTPVisualOffsetType(ins, sltpHolder);
                            const tpVisualValue = SlTpUtils.toVisualValue(sltpHolder.TakeProfitPriceValue, ins, tpVisualOffsetType);
                            const tpVisualValueFormatted = SlTpUtils.formatSLTPValue(tpVisualValue, ins, tpVisualOffsetType);
                            let tpVisualValueDecsr = Resources.getResource(SlTpUtils.getLocalizationForOffsetMode(tpVisualOffsetType));
                            if (isValidString(tpVisualValueDecsr)) {
                                tpVisualValueDecsr = ` ${tpVisualValueDecsr}`;
                            }
                            tpLabel = { Label: tpText, Value: `${tpVisualValueFormatted} ${tpVisualValueDecsr}` };
                            tpTrigger = {
                                Label: 'TP trigger',
                                Value: SLTPTrigger.GetTextValue(triggersShV, operationType, false)
                            };
                        }
                        if (paramDict.RealTrStopPrice && !stopPrice.Value) {
                            stopPrice.Value = paramDict.RealTrStopPrice;
                        }
                        if (orderType === OrderType.TrailingStop && !stopPrice.Value) {
                            const visualOffsetType = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(ins);
                            const visualOffset = SlTpUtils.toVisualValue(reqData.parameterDict.trailingStop, ins, visualOffsetType);
                            const visualOffsetStr = SlTpUtils.formatSLTPValue(visualOffset, ins, visualOffsetType);
                            stopPrice.Value = visualOffsetStr + ' (offset)';
                        }
                    }
                    let selectedLabels = [];
                    if (modifier !== undefined) {
                        // есть параметр - ключ локализации, дающий представление о том, какое действие было совершено
                        if (modifier === this.PLACE_OR_REPLACE || modifier === this.CHANGE_TO_MARKET) {
                            if (obj.position != null) {
                                boundTo.Value = obj.position.PositionId;
                                header = 'reports.Position modify request'; // если удастся ниже уточним что за модификация
                                // SL
                                const slOrder = obj.position.SLOrder;
                                const iSlTpParams = obj.parameterDict.sltp;
                                const sltpHolder = iSlTpParams.sltpHolder;
                                if (!isNaN(sltpHolder.StopLossPriceValue) || !isNullOrUndefined(slOrder)) {
                                    // SL
                                    stopPrice.Value = !isNaN(sltpHolder.StopLossPriceValue)
                                        ? sltpHolder.StopLossPriceValue
                                        : slOrder.Price;
                                    const isAllowOTHTrading = !isNullOrUndefined(iSlTpParams.isAllowOTHTradingSL)
                                        ? iSlTpParams.isAllowOTHTradingSL
                                        : slOrder === null || slOrder === void 0 ? void 0 : slOrder.IsAllowOTHTrading;
                                    if (!isNullOrUndefined(isAllowOTHTrading)) {
                                        overnight.Value = isAllowOTHTrading
                                            ? Resources.getResource('property.True')
                                            : Resources.getResource('property.False');
                                    }
                                    if (!isNaN(sltpHolder.StopLossPriceValue) &&
                                        sltpHolder.StopLossPriceType === SlTpPriceType.TrOffset) {
                                        if (sltpHolder.RealTrStopPrice) {
                                            stopPrice.Value = sltpHolder.RealTrStopPrice;
                                        }
                                        else {
                                            stopPrice.Value += ' (offset)';
                                        }
                                    }
                                    tpLabel = null;
                                    slLabel = null; // вместо SL Price будет отображаться только Price
                                    type.Value = 'reports.SL';
                                    if (isNaN(sltpHolder.StopLossPriceValue)) {
                                        // cancel SL
                                        header =
                                            obj.position.SLOrder.OrderType === OrderType.TrailingStop
                                                ? 'reports.Tr stop loss order cancel request'
                                                : 'reports.SL order cancel request';
                                        orderId.Value = obj.position.SLOrder.OrderNumber;
                                    }
                                    else if (obj.position.SLOrder) {
                                        // replace SL
                                        orderId.Value = obj.position.SLOrder.OrderNumber;
                                        if (sltpHolder.StopLossPriceType === SlTpPriceType.TrOffset ||
                                            obj.position.SLOrder.TrStopOffset > 0) {
                                            type.Value = 'reports.tr. stop';
                                            if (sltpHolder.StopLossPriceType === SlTpPriceType.TrOffset &&
                                                obj.position.SLOrder.TrStopOffset > 0) {
                                                header = 'reports.Tr stop loss order replace request';
                                            }
                                            else if (sltpHolder.StopLossPriceType !== SlTpPriceType.TrOffset) {
                                                header = 'reports.Tr stop loss order cancel request';
                                            }
                                            else {
                                                header = 'reports.Tr stop loss order placing request';
                                            }
                                        }
                                        else {
                                            header = 'reports.SL order replace request';
                                        }
                                    }
                                    else {
                                        // create SL
                                        header = 'reports.SL order placing request';
                                    }
                                }
                                // TP
                                const tpOrder = obj.position.TPOrder;
                                if ((!isNaN(sltpHolder.TakeProfitPriceValue) || !isNullOrUndefined(tpOrder)) &&
                                    !(obj.position.TPOrder &&
                                        obj.position.TPOrder.Price === sltpHolder.TakeProfitPriceValue)) {
                                    // была модификация SL, TP - не изменился
                                    price.Value = !isNaN(sltpHolder.TakeProfitPriceValue)
                                        ? sltpHolder.TakeProfitPriceValue
                                        : tpOrder.Price;
                                    const isAllowOTHTrading = !isNullOrUndefined(iSlTpParams.isAllowOTHTradingTP)
                                        ? iSlTpParams.isAllowOTHTradingTP
                                        : tpOrder === null || tpOrder === void 0 ? void 0 : tpOrder.IsAllowOTHTrading;
                                    if (!isNullOrUndefined(isAllowOTHTrading)) {
                                        overnight.Value = isAllowOTHTrading
                                            ? Resources.getResource('property.True')
                                            : Resources.getResource('property.False');
                                    }
                                    tpLabel = null;
                                    slLabel = null; // вместо TP Price будет отображаться только Price
                                    type.Value = 'reports.TP';
                                    if (obj.position.TPOrder) {
                                        orderId.Value = obj.position.TPOrder.OrderNumber;
                                    }
                                    if (isNaN(sltpHolder.TakeProfitPriceValue)) {
                                        header = 'reports.TP order cancel request'; // cancel TP
                                    }
                                    else if (obj.position.TPOrder) {
                                        header = 'reports.TP order replace request'; // replace TP
                                    }
                                    else {
                                        header = 'reports.TP order placing request'; // create TP
                                    }
                                }
                            }
                            else {
                                header =
                                    'reports.' +
                                        Resources.getResourceLang('property.' + OrderUtils.getOrderTypeLocalizationKey(orderType), LOCALE_EN) +
                                        ' order ' +
                                        (obj.order != null ? 'replace' : 'placing') +
                                        ' request';
                            }
                        }
                        else if (modifier === this.REVERSE_POSITION) {
                            header = 'reports.PositionReverseRequest';
                            const reverseOperation = {
                                Label: Resources.getResource('reports.operation'),
                                Value: Resources.getResource('AdditionalProperty.Reverse')
                            };
                            selectedLabels = [instrument, reverseOperation, amount, account, time, clientPanelId];
                        }
                    }
                    else {
                        // closing
                        header =
                            obj.isPosition != null
                                ? obj.isPosition
                                    ? 'reports.Position close request'
                                    : 'reports.' +
                                        Resources.getResourceLang('property.' + OrderUtils.getOrderTypeLocalizationKey(orderType), LOCALE_EN) +
                                        ' order cancel request'
                                : 'reports.' +
                                    Resources.getResourceLang('property.' + OrderUtils.getOrderTypeLocalizationKey(orderType), LOCALE_EN) +
                                    ' order placing request';
                    }
                    if (selectedLabels.length === 0) {
                        selectedLabels = [
                            instrument,
                            operation,
                            type,
                            overnight,
                            cash,
                            amount,
                            price,
                            stopPrice,
                            leverage,
                            tif,
                            operation2,
                            stopPriceForSecondOrder,
                            limitPriceForSecondOrder,
                            slLabel,
                            sllLabel,
                            slTrigger,
                            tpLabel,
                            tpTrigger,
                            instrType,
                            route,
                            user,
                            account,
                            orderId,
                            positionId,
                            boundTo,
                            productType,
                            time,
                            clientPanelId
                        ];
                    }
                    const resultDataArr = [];
                    const len = selectedLabels.length;
                    for (let i = 0; i < len; i++) {
                        const lbl = selectedLabels[i];
                        if (lbl && (lbl.Value || lbl.Value === 0)) {
                            resultDataArr.push([lbl.Label, lbl.Value]);
                        }
                    }
                    const reportMessage = new DirectReportMessage(header, resultDataArr);
                    reportMessage.SkipOnTicketViewer = true; // чтобы не показывать в TicketViewer
                    reportMessage.Instrument = ins;
                    reportMessage.account = acc;
                    reportMessage.productType = prodType;
                    ins.DataCache.NewMessage(reportMessage);
                }
            }
            catch (e) {
                ErrorInformationStorage.GetException(e);
            }
        };
        this.getKeyByValue = (obj, val) => {
            for (const key in obj) {
                if (obj[key] === val) {
                    return key;
                }
            }
            return null;
        };
    }
}
export const OrderConfirmation = new _OrderConfirmation();
